import { Injectable, inject } from '@angular/core';
import { Observable, of, catchError, map, switchMap } from 'rxjs';
import { ResourceService } from '../core/state/resource/resource.service';
import { TranscodingStatus } from '../core/state/resource/models';

export type VideoEngineType = 'shaka' | 'iframe' | 'pdf' | 'link' | 'native';

export interface PlaybackSourceDescriptor {
  engine: VideoEngineType;
  src: string;
  mimeType?: string;
}

@Injectable({
  providedIn: 'root',
})
export class VideoSourceResolverService {
  private resourceService = inject(ResourceService);

  resolveUrl(url: string): PlaybackSourceDescriptor {
    if (!url) {
      return { engine: 'native', src: '' };
    }

    const trimmed = url.trim();

    // 1. PDF Documents (external or uploaded)
    if (/\.pdf($|[?#])/i.test(trimmed) || trimmed.includes('/pdf/')) {
      return {
        engine: 'pdf',
        src: trimmed,
      };
    }

    // 2. YouTube
    const ytMatch = trimmed.match(
      /(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=|shorts\/|live\/))([\w-]{11})/,
    );
    if (ytMatch) {
      return {
        engine: 'iframe',
        src: `https://www.youtube.com/embed/${ytMatch[1]}?autoplay=1&rel=0&enablejsapi=1`,
      };
    }

    // 3. Vimeo
    const vimeoMatch = trimmed.match(/vimeo\.com\/(?:video\/)?(\d+)/);
    if (vimeoMatch) {
      return {
        engine: 'iframe',
        src: `https://player.vimeo.com/video/${vimeoMatch[1]}?autoplay=1`,
      };
    }

    // 4. Google Drive View/Preview link
    const gDriveMatch = trimmed.match(/drive\.google\.com\/file\/d\/([a-zA-Z0-9_-]+)/);
    if (gDriveMatch) {
      return {
        engine: 'iframe',
        src: `https://drive.google.com/file/d/${gDriveMatch[1]}/preview`,
      };
    }

    // 5. Manifests & Video Streams
    if (trimmed.includes('.mpd') || trimmed.includes('/manifest')) {
      return {
        engine: 'shaka',
        src: trimmed,
        mimeType: 'application/dash+xml',
      };
    }

    if (trimmed.includes('.m3u8')) {
      return {
        engine: 'shaka',
        src: trimmed,
        mimeType: 'application/x-mpegURL',
      };
    }

    if (trimmed.match(/\.(mp4|webm|ogg|mov|m4v|mkv)(\?|$)/i)) {
      return {
        engine: 'shaka',
        src: trimmed,
      };
    }

    // Cloud storage & media delivery endpoints
    if (
      /storage\.googleapis\.com|s3[.-]|amazonaws\.com|wasabisys\.com|digitaloceanspaces\.com|b-cdn\.net|cloudfront\.net|:9000/i.test(
        trimmed,
      )
    ) {
      return {
        engine: 'shaka',
        src: trimmed,
      };
    }

    // 6. External Web Links (non-video URLs)
    if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
      return {
        engine: 'link',
        src: trimmed,
      };
    }

    return {
      engine: 'shaka',
      src: trimmed,
    };
  }

  resolveResource(resourceId: string): Observable<PlaybackSourceDescriptor> {
    const trimmedId = resourceId.trim();

    if (
      trimmedId.startsWith('http://') ||
      trimmedId.startsWith('https://')
    ) {
      return of(this.resolveUrl(trimmedId));
    }

    const fallbackSignedUrl = (): Observable<PlaybackSourceDescriptor> => {
      return this.resourceService.getSignedUrl(trimmedId).pipe(
        map((data) => {
          const descriptor = this.resolveUrl(data.signedUrl);
          return descriptor.engine === 'link'
            ? { engine: 'shaka' as VideoEngineType, src: data.signedUrl }
            : descriptor;
        }),
        catchError(() =>
          of({
            engine: 'native' as VideoEngineType,
            src: '',
          }),
        ),
      );
    };

    return this.resourceService.getById(trimmedId).pipe(
      switchMap((res) => {
        const resourceType = (res.type || '').toLowerCase();

        if (resourceType === 'document') {
          return of({
            engine: 'pdf' as VideoEngineType,
            src: res.path || '',
          });
        }

        if (resourceType === 'link') {
          return of({
            engine: 'link' as VideoEngineType,
            src: res.path || '',
          });
        }

        if (
          res.path?.startsWith('http://') ||
          res.path?.startsWith('https://') ||
          res.provider?.toLowerCase() === 'external'
        ) {
          const descriptor = this.resolveUrl(res.path || '');
          if (resourceType === 'video' && descriptor.engine === 'link') {
            return of({ engine: 'shaka' as VideoEngineType, src: res.path || '' });
          }
          return of(descriptor);
        }

        const isCompleted =
          res.transcodingStatus === TranscodingStatus.COMPLETED;

        if (isCompleted) {
          const provider = res.provider?.toLowerCase();
          if (provider === 'bunny') {
            return this.resourceService.getSignedUrl(trimmedId, true).pipe(
              map((data) => ({
                engine: 'shaka' as VideoEngineType,
                src: data.signedUrl,
                mimeType: 'application/x-mpegURL',
              })),
              catchError(() => fallbackSignedUrl()),
            );
          }
          return of({
            engine: 'shaka' as VideoEngineType,
            src: this.resourceService.getManifestUrl(trimmedId),
            mimeType: 'application/dash+xml',
          });
        }

        return fallbackSignedUrl();
      }),
      catchError(() => fallbackSignedUrl()),
    );
  }
}
